import { type RadarSeriesOption } from 'echarts';
import { type RadarOption } from 'echarts/types/src/coord/radar/RadarModel';
import { type ThemeParser } from '../../../core/theme';
import { overrideIfUndefined } from '../../../util/data';
import { type Option } from '../../../core/option';
import { isObject } from '../../../util/utils';

export function judgeIsNormalDvRadar(option: Option) {
  if (typeof option?.series === 'undefined') {
    return false;
  }
  const dvRadarSeries = option.series.filter(item => item.type === 'dvRadar');
  if (dvRadarSeries.length === 0) {
    return false;
  }
  return true;
}

export function parseDvRadarColor(
  seriesOption: RadarSeriesOption[],
  ...args: Parameters<ThemeParser>
) {
  const [, , optionToken] = args;

  seriesOption.forEach(option => {
    const DvRadarSeriesColor = optionToken.dvRadar.seriesColor;
    if (DvRadarSeriesColor) {
      const DvRadarSeriesColorLen = DvRadarSeriesColor.length;
      option.data.forEach((item, data_index) => {
        const idx = data_index % DvRadarSeriesColorLen;
        const themeDvRadarColor = optionToken.dvRadar.seriesColor[idx];
        if (!isObject(item.itemStyle)) {
          const itemStyle = {
            color: themeDvRadarColor
          };
          item.itemStyle = itemStyle;
        } else {
          item.itemStyle.color = overrideIfUndefined(item.itemStyle.color, themeDvRadarColor);
        }
      });
    }
  });
}

export function parseDvRadarLineStyle(
  seriesOption: RadarSeriesOption[],
  ...args: Parameters<ThemeParser>
) {
  const [, , optionToken] = args;

  seriesOption.forEach(option => {
    const DvRadarSeriesLineStyle = optionToken.dvRadar.radarLineStyle;
    if (DvRadarSeriesLineStyle) {
      const DvRadarSeriesLineStyleLen = DvRadarSeriesLineStyle.length;
      option.data.forEach((item, data_index) => {
        const idx = data_index % DvRadarSeriesLineStyleLen;
        const themeDvRadarLine = optionToken.dvRadar.radarLineStyle[idx];
        if (!isObject(item.lineStyle)) {
          item.lineStyle = themeDvRadarLine;
        } else {
          item.lineStyle = overrideIfUndefined(item.itemStyle, themeDvRadarLine);
        }
      });
    }
  });
}

export function parseDvRadarAreaStyle(
  seriesOption: RadarSeriesOption[],
  ...args: Parameters<ThemeParser>
) {
  const [, , optionToken] = args;

  seriesOption.forEach(option => {
    const DvRadarSeriesAreaStyle = optionToken.dvRadar.areaStyle;
    if (DvRadarSeriesAreaStyle) {
      const DvRadarSeriesAreaStyleLen = DvRadarSeriesAreaStyle.length;
      option.data.forEach((item, data_index) => {
        const idx = data_index % DvRadarSeriesAreaStyleLen;
        const themeDvRadarArea = optionToken.dvRadar.areaStyle[idx];
        if (!isObject(item.areaStyle)) {
          item.areaStyle = themeDvRadarArea;
        } else {
          item.areaStyle = overrideIfUndefined(item.itemStyle, themeDvRadarArea);
        }
      });
    }
  });
}

export function parseDvRadarSymbolSize(
  seriesOption: RadarSeriesOption[],
  ...args: Parameters<ThemeParser>
) {
  const [, , optionToken] = args;

  seriesOption.forEach(option => {
    const themeDvRadarSymbolSize = optionToken.dvRadar.symbolSize;
    if (!isObject(option.symbolSize)) {
      option.symbolSize = themeDvRadarSymbolSize;
    } else {
      option.symbolSize = overrideIfUndefined(option.symbolSize, themeDvRadarSymbolSize);
    }
  });
}

export function parseRadarSysShape(radarSystems: RadarOption[], ...args: Parameters<ThemeParser>) {
  const [, , optionToken] = args;

  radarSystems.forEach(radar => {
    const themeRadarSysShape = optionToken.dvRadar.radarSysShape;
    radar.shape = overrideIfUndefined(radar.shape, themeRadarSysShape);
  });
}

export function parseRadarSysAxisLine(
  radarSystems: RadarOption[],
  ...args: Parameters<ThemeParser>
) {
  const [, , optionToken] = args;

  radarSystems.forEach(radar => {
    const themeRadarSysLine = optionToken.dvRadar.radarSysLine;
    if (!isObject(radar.axisLine)) {
      radar.axisLine = themeRadarSysLine;
    } else {
      radar.axisLine = overrideIfUndefined(radar.axisLine, themeRadarSysLine);
    }
  });
}

export function parseRadarSysAxisArea(
  radarSystems: RadarOption[],
  ...args: Parameters<ThemeParser>
) {
  const [, , optionToken] = args;

  radarSystems.forEach(radar => {
    const themeRadarSysArea = optionToken.dvRadar.radarSysArea;
    if (!isObject(radar.splitArea)) {
      radar.splitArea = themeRadarSysArea;
    } else {
      radar.splitArea = overrideIfUndefined(radar.splitArea, themeRadarSysArea);
    }
  });
}

export function parseRadarSysName(radarSystems: RadarOption[], ...args: Parameters<ThemeParser>) {
  const [, , optionToken] = args;

  radarSystems.forEach(radar => {
    const themeRadarSysName = optionToken.dvRadar.radarSysName;
    if (!isObject(radar.name)) {
      radar.name = themeRadarSysName;
    } else {
      radar.name = overrideIfUndefined(radar.name, themeRadarSysName);
    }
  });
}

export function parseRadarSysRadius(radarSystems: RadarOption[], ...args: Parameters<ThemeParser>) {
  const [, , optionToken] = args;

  radarSystems.forEach(radar => {
    const themeRadarSysRadius = optionToken.dvRadar.radarSysRadius;
    radar.radius = overrideIfUndefined(radar.radius, themeRadarSysRadius);
  });
}

/**
 * 移动端主题-柱状图解析器
 */
export function dvRadarThemeParse(...args: Parameters<ThemeParser>) {
  const [option] = args;
  if (!judgeIsNormalDvRadar(option)) {
    return;
  }
  const dvRadarSeries = option.series.filter(item => item.type === 'dvRadar');
  parseDvRadarColor(dvRadarSeries, ...args);
  parseDvRadarLineStyle(dvRadarSeries, ...args);
  parseDvRadarAreaStyle(dvRadarSeries, ...args);
  parseDvRadarSymbolSize(dvRadarSeries, ...args);

  // 雷达图坐标系
  let radarSys = option.radar as RadarOption[];
  if (!Array.isArray(radarSys)) {
    radarSys = [radarSys];
  }
  if (radarSys) {
    parseRadarSysShape(radarSys, ...args);
    parseRadarSysAxisLine(radarSys, ...args);
    parseRadarSysAxisArea(radarSys, ...args);
    parseRadarSysName(radarSys, ...args);
    parseRadarSysRadius(radarSys, ...args);
  }
}
